import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Download, Printer } from "lucide-react";
import DOMPurify from "dompurify";
import type { PayrollRecord } from "@/hooks/usePayroll";
import grx10Logo from "@/assets/grx10-logo.webp";
import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { normalizePayslip } from "@/lib/payslip-utils";
import { numberToWords } from "@/lib/number-to-words";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useUserOrganization } from "@/hooks/useUserOrganization";
import { useEmployeeDetails } from "@/hooks/useEmployeeDetails";
import React from "react";

/**
 * Convert imported asset URL to an inline data URL for use in detached
 * windows/iframes. Module-level cache so the canvas conversion runs once
 * per session — opening multiple payslips no longer redoes the work.
 */
const _logoDataUrlCache = new Map<string, string>();
function useLogoDataUrl(src: string) {
  const [dataUrl, setDataUrl] = useState<string>(() => _logoDataUrlCache.get(src) ?? "");
  useEffect(() => {
    if (_logoDataUrlCache.has(src)) {
      setDataUrl(_logoDataUrlCache.get(src)!);
      return;
    }
    let cancelled = false;
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      if (cancelled) return;
      try {
        const canvas = document.createElement("canvas");
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
        const ctx = canvas.getContext("2d");
        if (ctx) {
          ctx.drawImage(img, 0, 0);
          const url = canvas.toDataURL("image/png");
          _logoDataUrlCache.set(src, url);
          setDataUrl(url);
        }
      } catch { /* CORS fallback */ }
    };
    img.src = src;
    return () => { cancelled = true; };
  }, [src]);
  return dataUrl;
}

const fmtFull = (value: number) =>
  `₹${value.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const fmt = (value: number) => `₹${value.toLocaleString("en-IN")}`;

/**
 * Fetch branding and org identity info via React Query so multiple dialog
 * opens in the same session reuse the cache. Keyed by user id; data is
 * static for the lifetime of the session in 99% of cases.
 */
function useBrandingInfo(userId: string | undefined, organizationId: string | undefined) {
  const { data } = useQuery({
    queryKey: ["payslip-branding", userId, organizationId],
    enabled: !!userId,
    staleTime: 30 * 60_000,
    gcTime: 60 * 60_000,
    queryFn: async () => {
      let orgId = organizationId;
      if (!orgId) {
        const { data: profile } = await supabase
          .from("profiles")
          .select("organization_id")
          .eq("user_id", userId!)
          .maybeSingle();
        orgId = profile?.organization_id ?? undefined;
      }
      if (!orgId) {
        return { color: "#e11d74", companyName: "", companyAddress: "" };
      }
      const [{ data: compliance }, { data: org }] = await Promise.all([
        supabase
          .from("organization_compliance")
          .select("brand_color, legal_name, registered_address, state, pincode")
          .eq("organization_id", orgId)
          .maybeSingle(),
        supabase
          .from("organizations")
          .select("name")
          .eq("id", orgId)
          .maybeSingle(),
      ]);
      const c = compliance as any;
      // registered_address often already ends with the state + pincode, so
      // only append them when they aren't already present to avoid the
      // "…, Karnataka - 560008, Karnataka, 560008" duplication.
      const addr = String(c?.registered_address || "").trim();
      const addrLower = addr.toLowerCase();
      const extra = [c?.state, c?.pincode].filter(
        (v) => v && !addrLower.includes(String(v).toLowerCase())
      );
      const parts = [addr, ...extra].filter(Boolean);
      return {
        color: c?.brand_color || "#e11d74",
        companyName: c?.legal_name || (org as any)?.name || "",
        companyAddress: parts.join(", "),
      };
    },
  });
  return {
    color: data?.color ?? "#e11d74",
    companyName: data?.companyName ?? "",
    companyAddress: data?.companyAddress ?? "",
  };
}

const periodLabel = (p: string) => {
  const [y, m] = p.split("-");
  const months = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
  return `${months[parseInt(m) - 1]} ${y}`;
};

interface PaySlipDialogProps {
  record: PayrollRecord | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function PaySlipDialog({ record, open, onOpenChange }: PaySlipDialogProps) {
  const logoDataUrl = useLogoDataUrl(grx10Logo);
  const { user } = useAuth();
  const { data: orgData } = useUserOrganization();
  const { color: brandColor, companyName, companyAddress } = useBrandingInfo(user?.id, orgData?.organizationId);
  const { data: employeeDetails } = useEmployeeDetails(record?.profile_id ?? null);
  if (!record) return null;

  const slip = normalizePayslip(record);
  const { earnings, deductions, totalEarnings, totalDeductions, netPay, lopDays, workingDays, paidDays } = slip;

  const r = record as any;
  const ed = employeeDetails as any;
  const employeeName  = DOMPurify.sanitize(record.profiles?.full_name || "Employee");
  const jobTitle      = DOMPurify.sanitize(record.profiles?.job_title || ed?.designation || "—");
  const department    = DOMPurify.sanitize(record.profiles?.department || r.department || "—");
  const employeeId    = DOMPurify.sanitize(record.profiles?.employee_id || ed?.employee_code || r.employee_id || "—");
  const panNumber     = DOMPurify.sanitize(ed?.pan || ed?.pan_number || r.pan_number || "—");
  const aadhaarNumber = DOMPurify.sanitize(ed?.aadhaar_masked || ed?.aadhaar_last_four || "—");
  const bankAccount   = DOMPurify.sanitize(ed?.bank_account || ed?.bank_account_number || r.bank_account_number || "—");
  const bankIfsc      = DOMPurify.sanitize(ed?.ifsc || ed?.bank_ifsc || r.bank_ifsc || "—");
  const uanRaw        = (ed?.uan || ed?.uan_number || r.uan_number || "").trim();
  const isUanOptedOut = uanRaw.toLowerCase() === "opted out";
  const uanNumber     = DOMPurify.sanitize(uanRaw || "—");
  // Pull the PF account number from the employee's profile (same source as
  // PAN/Aadhaar), not a hardcoded establishment code. "Opted out" of UAN → no PF.
  const pfNumber      = DOMPurify.sanitize(isUanOptedOut ? uanRaw : (ed?.pf_number || r.pf_number || "—"));
  const gender        = DOMPurify.sanitize(ed?.gender || r.gender || "—");
  const location      = DOMPurify.sanitize(record.profiles?.location || ed?.work_location || r.location || "—");
  // Use the profile's join_date first, then ED's joining_date as fallback.
  const rawJoinDate   = record.profiles?.join_date || ed?.joining_date || r.date_of_joining || "";
  const dateOfJoining = rawJoinDate
    ? DOMPurify.sanitize(new Date(rawJoinDate).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }))
    : "—";
  const period        = periodLabel(record.pay_period);
  const genDate       = new Date().toLocaleDateString("en-IN", { day: "2-digit", month: "long", year: "numeric" });
  const logoSrc       = logoDataUrl || new URL(grx10Logo, window.location.origin).href;

  // Pad earnings/deductions to equal rows for side-by-side table
  const maxRows = Math.max(earnings.length, deductions.length);
  const padE = [...earnings,    ...Array(maxRows - earnings.length).fill(null)];
  const padD = [...deductions,  ...Array(maxRows - deductions.length).fill(null)];

  // Helper: derive tint backgrounds from brand color
  const hexToRgb = (hex: string) => {
    const h = hex.replace("#", "");
    return { r: parseInt(h.slice(0,2),16), g: parseInt(h.slice(2,4),16), b: parseInt(h.slice(4,6),16) };
  };
  const rgb     = hexToRgb(brandColor);
  const tintBg  = `rgba(${rgb.r},${rgb.g},${rgb.b},0.06)`;
  const tintMed = `rgba(${rgb.r},${rgb.g},${rgb.b},0.12)`;

  // Employee info table: 4 columns (label|value|label|value)
  const empFields = [
    ["Employee ID",    employeeId,               "Pay Period",   period],
    ["Designation",    jobTitle,                  "Department",   department],
    ["Gender",         gender,                    "Location",     location],
    ["Date of Joining",dateOfJoining,             "Working Days", String(workingDays || "—")],
    ["Paid Days",      String(paidDays || "—"),   "LOP Days",     String(lopDays || "0")],
    ["PAN No",         panNumber,                 "Aadhaar No",   aadhaarNumber],
    ["UAN No",         uanNumber,                 "PF No",        pfNumber],
    ["Bank A/C No",    bankAccount,               "IFSC Code",    bankIfsc],
  ];

  const buildHTML = () => {
    const bc  = brandColor;
    const esc = (v: unknown) => DOMPurify.sanitize(String(v ?? ""));

    const eRows = padE.map((e, i) => {
      const d   = padD[i];
      const bg  = i % 2 === 0 ? "#fff" : tintBg;
      return `<tr style="background:${bg}">
        <td class="cell">${e ? esc(e.label) : ''}</td>
        <td class="cell r">${e && e.amount > 0 ? fmtFull(e.amount) : e ? '--' : ''}</td>
        <td class="cell mid">${d ? esc(d.label) : ''}</td>
        <td class="cell r">${d && d.amount > 0 ? fmtFull(d.amount) : d ? '--' : ''}</td>
      </tr>`;
    }).join("");

    const empRows = empFields.map(([l1, v1, l2, v2]) => `
      <tr>
        <td class="eg-label">${l1}</td><td class="eg-value">${esc(v1)}</td>
        <td class="eg-label">${l2}</td><td class="eg-value">${esc(v2)}</td>
      </tr>`).join("");

    return `<!DOCTYPE html>
<html><head><meta charset="utf-8">
<title>Pay Slip — ${employeeName} — ${esc(period)}</title>
<style>
  @page { size: A4; margin: 14mm 16mm; }
  * { margin: 0; padding: 0; box-sizing: border-box; }
  body { font-family: 'Segoe UI', system-ui, Arial, sans-serif; color: #1a1a1a; background: #fff;
         width: 700px; max-width: 700px; margin: 0 auto;
         -webkit-print-color-adjust: exact; print-color-adjust: exact; }

  /* Header */
  .header { background: ${bc}; padding: 18px 24px; display: flex; justify-content: space-between; align-items: center; }
  .co-logo-wrap { background: #fff; border-radius: 6px; padding: 5px 8px; display: flex; align-items: center; justify-content: center; }
  .co-logo  { height: 38px; width: auto; object-fit: contain; display: block; }
  .co-info  { display: flex; align-items: center; gap: 14px; }
  .co-name  { font-size: 20px; font-weight: 800; color: #fff; text-transform: uppercase; letter-spacing: 1.5px; line-height: 1.2; }
  .co-addr  { font-size: 10px; color: rgba(255,255,255,0.82); margin-top: 4px; line-height: 1.6; max-width: 340px; }
  .slip-title  { font-size: 18px; font-weight: 700; color: #fff; letter-spacing: 2px; text-transform: uppercase; text-align: right; white-space: nowrap; }
  .slip-period { font-size: 11px; color: rgba(255,255,255,0.85); margin-top: 4px; font-style: italic; text-align: right; }

  /* Employee name bar */
  .emp-name-bar { background: ${tintMed}; padding: 8px 14px; border-left: 4px solid ${bc}; margin-top: 14px; }
  .emp-name-bar .name { font-size: 15px; font-weight: 700; color: ${bc}; }
  .emp-name-bar .sub  { font-size: 11px; color: #555; margin-top: 2px; }

  /* Employee details grid */
  .emp-table { width: 100%; border-collapse: collapse; border: 1px solid #ddd; }
  .eg-label { font-size: 10px; font-weight: 700; color: #444; text-transform: uppercase; letter-spacing: 0.4px;
               padding: 5px 10px; background: ${tintBg}; border-bottom: 1px solid #e8e8e8; border-right: 1px solid #ddd; width: 18%; }
  .eg-value { font-size: 11px; color: #222; padding: 5px 10px;
               border-bottom: 1px solid #e8e8e8; border-right: 1px solid #ddd; width: 32%; }
  .emp-table tr:last-child .eg-label,
  .emp-table tr:last-child .eg-value { border-bottom: none; }

  /* Earnings & Deductions */
  .ed-table { width: 100%; border-collapse: collapse; margin-top: 14px; border: 1px solid #ddd; }
  .ed-table thead th { background: ${bc}; color: #fff; font-size: 11px; font-weight: 700;
                        padding: 7px 10px; text-transform: uppercase; letter-spacing: 0.5px; text-align: left; }
  .ed-table thead th.r { text-align: right; }
  .cell { padding: 5px 10px; font-size: 11px; border-bottom: 1px solid #eee; color: #222; }
  .cell.r   { text-align: right; font-weight: 600; font-variant-numeric: tabular-nums; }
  .cell.mid { border-left: 1px solid #ddd; }
  .total-row td { border-top: 2px solid ${bc}; font-weight: 700; font-size: 11px;
                   padding: 7px 10px; background: ${tintBg}; }
  .earn { color: #15803d; }
  .ded  { color: #dc2626; }

  /* Net Pay */
  .net-box { background: ${bc}; padding: 14px 20px; margin-top: 14px;
              display: flex; justify-content: space-between; align-items: center; }
  .net-label { color: rgba(255,255,255,0.9); font-size: 11px; text-transform: uppercase;
                letter-spacing: 1px; font-weight: 700; }
  .net-sub   { color: rgba(255,255,255,0.7); font-size: 10px; font-style: italic; margin-top: 3px; }
  .net-amt   { color: #fff; font-size: 26px; font-weight: 800; letter-spacing: -0.5px; }
  .words-bar { background: ${tintBg}; border: 1px solid #ddd; border-top: none;
                text-align: center; font-size: 11px; font-weight: 600; color: #333; padding: 7px 10px; }

  /* Footer */
  .footer { margin-top: 18px; border-top: 2px solid ${bc}; padding-top: 10px;
             display: flex; justify-content: space-between; align-items: center; }
  .gen-date  { font-size: 10px; color: #888; }
  .footer-sys { font-size: 10px; color: #888; font-style: italic; text-align: right; }
  .statutory { margin-top: 10px; border: 1px solid #ddd; border-radius: 3px; padding: 8px 12px; background: #fafafa; }
  .statutory-title { font-size: 9px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.6px; color: #555; margin-bottom: 5px; }
  .statutory-items { margin: 0; padding: 0; list-style: none; }
  .statutory-items li { font-size: 9px; color: #666; line-height: 1.5; padding-left: 10px; position: relative; margin-bottom: 2px; }
  .statutory-items li::before { content: "•"; position: absolute; left: 0; color: #aaa; }

  @media print { body { width: 100%; max-width: 100%; } }
</style></head><body>

  <div class="header">
    <div class="co-info">
      <div class="co-logo-wrap"><img src="${logoSrc}" alt="Logo" class="co-logo" /></div>
      <div>
        <div class="co-name">${esc(companyName)}</div>
        ${companyAddress ? `<div class="co-addr">${esc(companyAddress)}</div>` : ''}
      </div>
    </div>
    <div>
      <div class="slip-title">Pay Slip</div>
      <div class="slip-period">${esc(period)}</div>
    </div>
  </div>

  <div class="emp-name-bar">
    <div class="name">${employeeName}</div>
    <div class="sub">${esc(jobTitle)}${department && department !== '—' ? ' &nbsp;·&nbsp; ' + esc(department) : ''}</div>
  </div>

  <table class="emp-table"><tbody>${empRows}</tbody></table>

  <table class="ed-table">
    <thead>
      <tr>
        <th>Earning</th><th class="r">Amount (₹)</th>
        <th style="border-left:1px solid rgba(255,255,255,0.3)">Deduction</th><th class="r">Amount (₹)</th>
      </tr>
    </thead>
    <tbody>
      ${eRows}
      <tr class="total-row">
        <td>Total Earnings</td><td class="r earn">${fmtFull(totalEarnings)}</td>
        <td class="mid">Total Deductions</td><td class="r ded">${fmtFull(totalDeductions)}</td>
      </tr>
    </tbody>
  </table>

  <div class="net-box">
    <div>
      <div class="net-label">Total Net Payable</div>
      <div class="net-sub">Gross Earnings − Total Deductions</div>
    </div>
    <div class="net-amt">₹${Math.round(netPay).toLocaleString("en-IN")}/-</div>
  </div>
  <div class="words-bar">Amount in Words: ${numberToWords(netPay)}</div>

  <div class="footer">
    <div class="gen-date">Generated on ${genDate}</div>
    <div class="footer-sys">This is a system-generated payslip. No physical signature is required.</div>
  </div>
  <div class="statutory">
    <div class="statutory-title">Statutory Notice</div>
    <ul class="statutory-items">
      <li>Provident Fund (PF) contributions are deducted under the Employees&apos; Provident Funds &amp; Miscellaneous Provisions Act, 1952.</li>
      <li>Professional Tax (PT), where applicable, is deducted as per the applicable State legislation.</li>
      <li>Tax Deducted at Source (TDS) is computed and deducted under the Income Tax Act, 1961.</li>
      <li>This document is valid under the Information Technology Act, 2000 and the Payment of Wages Act, 1936.</li>
      <li>For any payroll discrepancies, please raise a query with the HR / Payroll team within 30 days of receipt of this payslip.</li>
    </ul>
  </div>

</body></html>`;
  };

  const openPrintWindow = () => {
    const html = buildHTML();
    const win = window.open("", "_blank", "width=750,height=950");
    if (!win) return;
    win.document.write(html);
    win.document.close();
    setTimeout(() => { win.focus(); win.print(); }, 500);
    win.onafterprint = () => win.close();
    const poll = setInterval(() => { if (win.closed) { clearInterval(poll); window.focus(); } }, 500);
  };

  const handleDownload = async () => {
    const r = record as any;

    // Engine path: payroll_entries records carry payroll_run_id.
    // Use server-side PDF generation and open the returned signed URL.
    if (r.payroll_run_id) {
      try {
        const { data, error } = await supabase.functions.invoke("generate-payslip", {
          body: { entry_id: record.id },
        });
        if (!error && data?.signed_url) {
          window.open(data.signed_url, "_blank", "noopener,noreferrer");
          return;
        }
        console.error("Server PDF generation failed:", error ?? data);
      } catch (err) {
        console.error("Edge Function invoke failed:", err);
      }
    }

    // Legacy path (payroll_records) or fallback: client-side html2pdf.js
    const container = document.createElement("div");
    container.style.cssText = "position:fixed;left:-10000px;top:0;width:700px;background:#ffffff;z-index:-1;";
    document.body.appendChild(container);

    const shadow = container.attachShadow({ mode: "open" });
    const parser = new DOMParser();
    const parsed = parser.parseFromString(buildHTML(), "text/html");
    const style = parsed.querySelector("style");
    if (style) shadow.appendChild(style.cloneNode(true));
    const bodyContent = document.createElement("div");
    bodyContent.innerHTML = parsed.body.innerHTML;
    shadow.appendChild(bodyContent);

    try {
      await new Promise<void>((resolve) => {
        const img = shadow.querySelector("img") as HTMLImageElement | null;
        if (img && !img.complete) {
          img.onload = () => resolve();
          img.onerror = () => resolve();
          setTimeout(resolve, 2000);
        } else {
          setTimeout(resolve, 400);
        }
      });
      const html2pdf = (await import("html2pdf.js")).default;
      await html2pdf()
        .set({
          margin: [10, 16, 20, 16],
          filename: `PaySlip_${employeeName.replace(/\s+/g, "_")}_${record.pay_period}.pdf`,
          image: { type: "jpeg", quality: 0.98 },
          html2canvas: { scale: 2, useCORS: true, backgroundColor: "#ffffff", width: 700, windowWidth: 700, scrollX: 0, scrollY: 0 },
          jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
        })
        .from(bodyContent)
        .save();
    } catch (err) {
      console.error("PDF generation failed, falling back to print:", err);
      openPrintWindow();
    } finally {
      if (container.parentNode) document.body.removeChild(container);
    }
  };

  // ── In-dialog React preview ───────────────────────────────────────────────
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto glass-morphism">
        <DialogHeader className="flex flex-row items-center justify-between">
          <DialogTitle className="text-gradient-primary text-xl">Pay Slip</DialogTitle>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={openPrintWindow}>
              <Printer className="h-4 w-4 mr-1" />Print
            </Button>
            <Button size="sm" onClick={handleDownload} className="bg-gradient-financial text-white hover:opacity-90">
              <Download className="h-4 w-4 mr-1" />Download PDF
            </Button>
          </div>
        </DialogHeader>

        <div className="space-y-3 text-sm">
          {/* Header */}
          <div className="rounded-md overflow-hidden">
            <div className="flex justify-between items-center p-4" style={{ background: brandColor }}>
              <div className="flex items-center gap-3">
                <div className="bg-white rounded p-1.5 flex items-center justify-center shrink-0">
                  <img src={grx10Logo} alt="Company Logo" className="h-9 w-auto object-contain block" />
                </div>
                <div>
                  <p className="text-base font-extrabold tracking-wider uppercase text-white">{companyName}</p>
                  {companyAddress && <p className="text-xs mt-1 leading-relaxed" style={{ color: "rgba(255,255,255,0.82)" }}>{companyAddress}</p>}
                </div>
              </div>
              <div className="text-right">
                <p className="text-base font-bold tracking-widest uppercase text-white whitespace-nowrap">Pay Slip</p>
                <p className="text-xs italic mt-1" style={{ color: "rgba(255,255,255,0.85)" }}>{period}</p>
              </div>
            </div>
          </div>

          {/* Employee name bar */}
          <div className="px-3 py-2 rounded-sm border-l-4" style={{ background: tintMed, borderLeftColor: brandColor }}>
            <p className="font-bold text-sm" style={{ color: brandColor }}>{employeeName}</p>
            <p className="text-xs text-muted-foreground mt-0.5">
              {jobTitle}{department && department !== "—" ? ` · ${department}` : ""}
            </p>
          </div>

          {/* Employee details grid */}
          <div className="border border-border rounded overflow-hidden">
            <table className="w-full text-xs">
              <tbody>
                {empFields.map(([l1, v1, l2, v2], i) => (
                  <tr key={i} className="border-b border-border/50 last:border-0">
                    <td className="px-3 py-1.5 font-semibold uppercase tracking-wide text-muted-foreground w-[18%]" style={{ background: tintBg }}>{l1}</td>
                    <td className="px-3 py-1.5 w-[32%]">{v1}</td>
                    <td className="px-3 py-1.5 font-semibold uppercase tracking-wide text-muted-foreground w-[18%] border-l border-border" style={{ background: tintBg }}>{l2}</td>
                    <td className="px-3 py-1.5">{v2}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Earnings & Deductions */}
          <div className="border border-border rounded overflow-hidden">
            <table className="w-full text-xs">
              <thead>
                <tr style={{ background: brandColor }}>
                  <th className="text-left px-3 py-2 font-bold uppercase tracking-wide text-white">Earning</th>
                  <th className="text-right px-3 py-2 font-bold uppercase tracking-wide text-white">Amount (₹)</th>
                  <th className="text-left px-3 py-2 font-bold uppercase tracking-wide text-white border-l border-white/20">Deduction</th>
                  <th className="text-right px-3 py-2 font-bold uppercase tracking-wide text-white">Amount (₹)</th>
                </tr>
              </thead>
              <tbody>
                {padE.map((e, i) => {
                  const d = padD[i];
                  return (
                    <tr key={i} className="border-b border-border/40 last:border-0"
                        style={i % 2 !== 0 ? { background: tintBg } : undefined}>
                      <td className="px-3 py-1.5">{e ? e.label : ""}</td>
                      <td className="px-3 py-1.5 text-right font-semibold tabular-nums">{e && e.amount > 0 ? fmt(e.amount) : e ? "--" : ""}</td>
                      <td className="px-3 py-1.5 border-l border-border">{d ? d.label : ""}</td>
                      <td className="px-3 py-1.5 text-right font-semibold tabular-nums">{d && d.amount > 0 ? fmt(d.amount) : d ? "--" : ""}</td>
                    </tr>
                  );
                })}
                <tr className="font-bold" style={{ background: tintBg, borderTop: `2px solid ${brandColor}` }}>
                  <td className="px-3 py-2">Total Earnings</td>
                  <td className="px-3 py-2 text-right text-emerald-600 dark:text-emerald-400 tabular-nums">{fmt(totalEarnings)}</td>
                  <td className="px-3 py-2 border-l border-border">Total Deductions</td>
                  <td className="px-3 py-2 text-right text-destructive tabular-nums">{fmt(totalDeductions)}</td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Net Pay */}
          <div className="rounded overflow-hidden">
            <div className="flex justify-between items-center px-5 py-3" style={{ background: brandColor }}>
              <div>
                <p className="text-xs font-bold uppercase tracking-widest text-white/90">Total Net Payable</p>
                <p className="text-xs italic mt-0.5 text-white/70">Gross Earnings − Total Deductions</p>
              </div>
              <span className="text-2xl font-extrabold text-white tracking-tight">
                ₹{Math.round(netPay).toLocaleString("en-IN")}/-
              </span>
            </div>
            <div className="text-center text-xs font-semibold py-2 border border-t-0 border-border" style={{ background: tintBg }}>
              Amount in Words: {numberToWords(netPay)}
            </div>
          </div>

          {/* Footer */}
          <div className="flex justify-between items-start pt-2" style={{ borderTop: `2px solid ${brandColor}` }}>
            <p className="text-xs text-muted-foreground">Generated on {genDate}</p>
            <p className="text-xs text-muted-foreground italic text-right max-w-xs leading-relaxed">
              This is a computer-generated payslip and does not require a physical signature.<br />
              Valid under the Information Technology Act, 2000 and the Payment of Wages Act, 1936.
            </p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
